import { DocumentData, getDocs, QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';
import React from 'react';
import styled from 'styled-components';
import { FilterItem, getQuery, OrderItem } from 'services';
import toast from 'react-hot-toast';
import { MONTHS } from 'app-constants';
import { Button, Splash, WList } from 'shared/Components';
import { useHistory } from 'react-router-dom';
import { DashboardRoutePath } from 'modules/dashboard/navigation';

import FilterControle from '../PayoutsScreen/Components/FilterControle';
import { Box, FlexContainer, Image, Row, Text } from '../../../../shared/Typhography';

const Container = styled.div`
  margin-top: 150px;
`;

interface RevenueArtistItem extends DocumentData {
  artist: number;
  company: number;
  total: number;
  id: string;
  name: string;
  profileUrl: string;
  share: number;
}

interface RevenueBase extends DocumentData {
  artist: number;
  company: number;
  total: number;
  date: Timestamp;
}

interface RevenueRowProps {
  data: QueryDocumentSnapshot<RevenueBase>;
  props?: {
    loadArtists: (key: string) => void;
    showArtists?: string[];
    artistsRevenue?: {
      [key: string]: QueryDocumentSnapshot<RevenueArtistItem>[];
    };
    artistRevenueState: {
      [key: string]: {
        toggled?: boolean;
        field?: 'artist' | 'company' | 'total' | 'name' | 'share';
      };
    };
    setArtistRevenueState: (value: {
      [key: string]: {
        toggled?: boolean;
        field?: 'artist' | 'company' | 'total' | 'name' | 'share';
      };
    }) => void;
  };
}

const OrderingCarrot = styled.span<{ direction: 'asc' | 'desc' }>`
  display: inline-block;
  transform: rotate(${({ direction }) => (direction == 'asc' ? '90deg' : '-90deg')});
`;

const dateParse = (date?: Date) => {
  if (!date) return 'Not Set';

  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const RevenueRow = ({ data, props }: RevenueRowProps) => {
  const navigate = useHistory();

  return (
    <FlexContainer
      flexDirection="column"
      width="100%"
      minHeight="64px"
      style={{ position: 'relative' }}
    >
      <Row style={{ height: '64px', padding: '0 10px' }}>
        <span style={{ flex: 1 }}>{dateParse(data?.data().date.toDate())}</span>
        <span style={{ flex: 1 }}>${data.data().artist.toFixed(5)}</span>
        <span style={{ flex: 1 }}>${data.data().total.toFixed(5)}</span>
        <span style={{ flex: 1 }}>
          <span
            style={{ margin: '0 5px', cursor: 'pointer' }}
            onClick={() => navigate.push(`/revenue/${data.id}/artists`)}
          >
            ARTISTS
          </span>
          <span
            style={{ margin: '0 5px', cursor: 'pointer' }}
            onClick={() => navigate.push(`/revenue/${data.id}/puzzles`)}
          >
            PUZZLES
          </span>
        </span>
      </Row>
      {props?.artistRevenueState[data?.id]?.toggled && (
        <FlexContainer
          flexDirection="column"
          width="100%"
          style={{ background: 'lightgrey', maxHeight: `${64 * 6}px`, overflowY: 'auto' }}
        >
          <Row
            style={{
              height: '64px',
              padding: '0 10px',
              background: 'lightgrey',
              position: 'sticky',
              top: 0,
              left: 0,
            }}
          >
            <span style={{ flex: 1 }}>Name</span>
            <span style={{ flex: 1 }}>Profile image</span>
            <span style={{ flex: 1 }}>Artist Royalty</span>
            <span style={{ flex: 1 }}>Total</span>
          </Row>
          {props?.artistsRevenue?.[data.id] &&
            props?.artistsRevenue?.[data.id]?.map((artistDoc) => {
              if (props?.showArtists?.length && !props?.showArtists?.includes(artistDoc?.id))
                return;

              return (
                <Row
                  style={{ height: '64px', padding: '0 10px' }}
                  key={`${data?.id}_${artistDoc?.id}`}
                >
                  <span style={{ flex: 1 }}>{artistDoc?.data().name}</span>
                  <span style={{ flex: 1 }}>
                    <Image width="48px" height="48px" src={artistDoc?.data()?.profileUrl} />
                  </span>
                  <span style={{ flex: 1 }}>$ {(artistDoc?.data()?.artist ?? 0).toFixed(2)}</span>
                  <span style={{ flex: 1 }}>$ {(artistDoc?.data()?.total ?? 0).toFixed(2)}</span>
                </Row>
              );
            })}
          {!props?.artistsRevenue?.[data.id] && <Splash />}
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

export default function RevenueScreen() {
  const navigate = useHistory();

  const [data, setData] = React.useState<QueryDocumentSnapshot<RevenueBase>[]>([]);
  const [isLoading, setLoading] = React.useState<boolean>(true);
  const [filters, setFilters] = React.useState<{
    firebase: FilterItem[];
    fields: { [key: string]: any };
  }>({ firebase: [], fields: {} });
  const [ordering, setOrdering] = React.useState<OrderItem[]>([]);

  const [fetchArtists] = React.useState<string[]>();
  const [artistRevenue, setArtistRevenue] = React.useState<{
    [key: string]: QueryDocumentSnapshot<RevenueArtistItem>[];
  }>({});
  const [artistRevenueState, setArtistRevenueState] = React.useState<{
    [key: string]: {
      toggled: boolean;
      field: null | 'artist' | 'company' | 'total' | 'name' | 'share';
    };
  }>({});

  const [total, setTotal] = React.useState<{
    loading?: boolean;
    dateRange?: { min: Date; max: Date };
    artist?: number;
    total?: number;
  }>({});

  const calculateTotal = async () => {
    try {
      const nextQuery = getQuery('revenue', filters.firebase, []);
      setTotal({ loading: true });
      const tTotal = { artist: 0, total: 0 };

      const revenueDocs = await getDocs(nextQuery);
      for (let i = 0; i < revenueDocs.docs?.length; i++) {
        const revenueDoc = revenueDocs?.docs[i];
        if (!fetchArtists || fetchArtists?.length == 0) {
          tTotal.artist += revenueDoc.data().artist;
          tTotal.total += revenueDoc.data().total;
        } else {
          const artistQuery = getQuery(
            `revenue/${revenueDoc.id}/artists`,
            [{ attribute: 'uid', value: fetchArtists, operator: 'array-contains-any' }],
            [],
          );
          const artistDocs = await getDocs(artistQuery);

          for (let i2 = 0; i2 < artistDocs?.docs?.length; i2++) {
            const artistDoc = artistDocs?.docs[i2];
            tTotal.artist += artistDoc.data().artist;
            tTotal.total += artistDoc.data().total;
          }
        }
      }
      setTotal(tTotal);
    } catch (err) {
      toast.error('Failed to calculate totals');
      console.log(err);
    }
  };

  const loadArtists = async (revenueUuid: string) => {
    if (revenueUuid in artistRevenue) return;
    try {
      const query = getQuery(`revenue/${revenueUuid}/artists`, [], []);
      const docs = await getDocs(query);
      setArtistRevenue({
        ...artistRevenue,
        [revenueUuid]: docs.docs as QueryDocumentSnapshot<RevenueArtistItem>[],
      });
    } catch (err) {
      toast.error(`Failed to fetch artist revenue`);
      console.log(err);
    }
  };

  const load = async (reset = false) => {
    //if (!reset) return;

    setLoading(true);
    try {
      const nextQuery = getQuery(
        'revenue',
        filters.firebase,
        ordering,
        data.length > 0 && !reset ? data.pop() : undefined,
        20,
      );
      const nextData = await getDocs(nextQuery);

      setData(
        reset
          ? (nextData.docs as QueryDocumentSnapshot<RevenueBase>[])
          : [...data, ...(nextData.docs as QueryDocumentSnapshot<RevenueBase>[])],
      );
    } catch (err) {
      toast.error('Failed to fetch');
    }
    setLoading(false);
  };

  const manageOrdering = (attribute: string) => {
    const orderingItem = ordering.find((item) => item.field === attribute);

    if (orderingItem) {
      if (orderingItem.type === 'asc')
        setOrdering(
          ordering.map((item) => (item?.field === attribute ? { ...item, type: 'desc' } : item)),
        );
      else setOrdering(ordering.filter((item) => item?.field !== attribute));

      return;
    }

    setOrdering([...ordering, { field: attribute, type: 'asc' }]);
  };

  /* Initial data load */
  React.useEffect(() => {
    calculateTotal();
  }, [filters.firebase, fetchArtists]);

  React.useEffect(() => {
    load(true);
  }, [filters.firebase, ordering]);

  return (
    <Container>
      <Box padding="0">
        <div style={{ padding: '10px' }}>
          <Row style={{ height: '64px', padding: '0 10px' }}>
            <span style={{ flex: 1 }}>Date Range</span>
            <span style={{ flex: 1 }}>Artist Royalty</span>
            <span style={{ flex: 1 }}>Total</span>
          </Row>
          <Row style={{ height: '64px', padding: '0 10px' }}>
            {total && !total?.loading && (
              <>
                <span style={{ flex: 1 }}>
                  {filters?.fields?.date?.startDate &&
                    filters?.fields?.date?.endDate &&
                    `${dateParse(filters.fields.date.startDate)} - ${dateParse(
                      filters.fields.date.endDate,
                    )}`}
                  {(!filters?.fields?.date?.startDate || !filters?.fields?.date?.endDate) &&
                    'Unset'}
                </span>
                <span style={{ flex: 1 }}>${total.artist?.toFixed(5)}</span>
                <span style={{ flex: 1 }}>${total.total?.toFixed(5)}</span>
              </>
            )}
            {(!total || total?.loading) && <span style={{ flex: 1 }}>Loading...</span>}
          </Row>
        </div>

        <FlexContainer
          padding="10px"
          flexDirection="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <div style={{ flex: 1 }}>
            <FilterControle
              loading={isLoading}
              onChange={(item) => {
                setFilters({
                  firebase: [
                    ...filters.firebase.filter((filter) => filter?.attribute !== item?.name),
                    { attribute: item?.name, value: item?.value, operator: '==' },
                  ],
                  fields: {
                    ...filters.fields,
                    [item.name]: item,
                  },
                });
              }}
              clear={(name: string) => {
                setFilters({
                  firebase: filters.firebase.filter((item: any) => item?.attribute !== name),
                  fields: {
                    ...filters.fields,
                    [name]: undefined,
                  },
                });
              }}
              values={filters.fields}
              keys={[
                {
                  name: 'date',
                  placeholder: 'Date Range',
                  options: [],
                  type: 'date-range',
                  onChange: (dates: [Date | null, Date | null]) => {
                    const start = dates['0'];
                    const end = dates['1'];

                    if (!start || !end) {
                      if (filters.firebase.find((fil) => fil.attribute === 'date')) {
                        setFilters({
                          firebase: filters.firebase.filter((fil) => fil.attribute !== 'date'),
                          fields: {
                            ...filters.fields,
                            date: undefined,
                          },
                        });
                      }
                      return;
                    }

                    end?.setDate(end.getDate() + 1);
                    end?.setMilliseconds(end.getMilliseconds() - 1);

                    let firebaseFilter: FilterItem[] = [...filters.firebase];

                    firebaseFilter = firebaseFilter.filter((item) => item?.attribute !== 'date');

                    if (start && end) {
                      firebaseFilter.push({
                        value: Timestamp.fromDate(start),
                        attribute: 'date',
                        operator: '>=',
                      });

                      firebaseFilter.push({
                        value: Timestamp.fromDate(end),
                        attribute: 'date',
                        operator: '<=',
                      });
                    }

                    setFilters({
                      firebase: firebaseFilter,
                      fields: {
                        ...filters.fields,
                        date: { startDate: start, endDate: end },
                      },
                    });
                  },
                },
              ]}
            />
          </div>
          <div>
            <Button
              width="128"
              height="50"
              onClick={() =>
                navigate.push(
                  `/${DashboardRoutePath.REVENUE_ANALYTICS}?artists=${
                    fetchArtists ? JSON.stringify(fetchArtists) : []
                  }&filters=${JSON.stringify(filters?.fields ?? {})}`,
                )
              }
            >
              <Text size={1.1} weight="bold" color="white" cursor="pointer">
                Analytics
              </Text>
            </Button>
          </div>
        </FlexContainer>

        <Row style={{ height: '64px', padding: '0 10px' }}>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('date')}
          >
            Date
            {ordering?.find((oitem) => oitem?.field === 'date') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'date')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('artist')}
          >
            Artist Royalty
            {ordering?.find((oitem) => oitem?.field === 'artist') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'artist')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span
            style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => manageOrdering('total')}
          >
            Total
            {ordering?.find((oitem) => oitem?.field === 'total') && (
              <OrderingCarrot
                direction={ordering?.find((oitem) => oitem?.field === 'total')?.type ?? 'asc'}
              >
                &#8227;
              </OrderingCarrot>
            )}
          </span>
          <span style={{ flex: 1, fontWeight: 600, cursor: 'pointer' }}>Details</span>
        </Row>
        <WList
          data={data}
          maxHeight={window.innerHeight - 300}
          itemHeight={64}
          itemComponent={RevenueRow}
          props={{
            loadArtists: loadArtists,
            showArtists: fetchArtists?.length ? fetchArtists : undefined,
            artistsRevenue: artistRevenue,
            artistRevenueState: artistRevenueState,
            setArtistRevenueState: setArtistRevenueState,
          }}
          scrollConfig={{
            fromBottom: 100,
            event: async () => {
              if (!isLoading) {
                load();
                console.log('TRIGGER LOAD');
              }
            },
          }}
        />
      </Box>
    </Container>
  );
}
